import React from "react";
import { Card, Col, Row, Table, Badge } from "react-bootstrap";

/**
 * In-dashboard wiki: how riders work across Lusuku.
 *
 * IMPORTANT — content policy for this file:
 *   - No credentials, API keys, tokens, connection strings.
 *   - No internal hostnames or ports that aren't already public.
 *   - No repo paths or source-file names in production-visible text —
 *     staff in production cannot open files; inline the facts instead.
 *   - Development-only guidance must be gated behind NODE_ENV so CRA
 *     dead-code-eliminates it from the production bundle.
 */
const IS_DEV = process.env.NODE_ENV === "development";

const ACCENT = "#5b6b8a";
const TINT = "#5b6b8a1a";
const PILLTEXT = "#3f4d66";

const titleStyle = { fontSize: 13.5, fontWeight: 700, color: "#1a2b4a" };
const bodyText = { fontSize: 12.5 };
const cardStyle = { border: "1px solid #e5e9f1", borderRadius: 8, boxShadow: "none" };
const theadStyle = { background: "#f5f7fb" };

const pillStyle = {
  display: "inline-block",
  padding: "2px 8px",
  borderRadius: 10,
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.3px",
  textTransform: "uppercase",
  background: TINT,
  color: PILLTEXT,
  marginRight: 6,
  marginBottom: 4,
};

const Pill = ({ label }) => <span style={pillStyle}>{label}</span>;

const SectionIcon = ({ cls }) => (
  <i
    className={cls}
    style={{ fontSize: 15, color: ACCENT, marginRight: 8, verticalAlign: "middle" }}
  />
);

const QA = ({ q, a }) => (
  <div style={{ marginBottom: 10 }}>
    <div style={{ fontSize: 12.5, fontWeight: 700, color: "#1a2b4a" }}>{q}</div>
    <div className="text-muted" style={{ fontSize: 12.5, marginTop: 1 }}>{a}</div>
  </div>
);

const RiderPlatformWiki = () => {
  return (
    <div>
      {/* Compact header — the tab container owns outer padding and the tab label */}
      <div style={{ borderLeft: `3px solid ${ACCENT}`, paddingLeft: 12, marginBottom: 18 }}>
        <div style={{ fontSize: 17, fontWeight: 700, color: "#1a2b4a" }}>
          Riders on Lusuku
        </div>
        <div style={{ fontSize: 12.5, color: "#6b7a99", marginTop: 2 }}>
          Onboarding, assignment, tracking and earnings — who delivers an
          order and how they get paid.
        </div>
      </div>

      {IS_DEV && (
        <div
          style={{
            background: "#fff8ea",
            border: "1px solid #f1e0a8",
            borderRadius: 8,
            padding: "10px 14px",
            marginBottom: 16,
            fontSize: 12.5,
          }}
        >
          <span style={{ fontWeight: 700, color: "#7a5a00" }}>
            Local development only.
          </span>{" "}
          <span className="text-muted">
            The backend ships a delivery-quote simulation that runs the real
            fee engine and prints the full fee build-up, split and balanced
            journal — use it to sanity-check pricing or split changes before
            they ship. API smoke-test steps live in the server docs, not in
            this UI (which is served to all supervisors in production).
          </span>
        </div>
      )}

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-delivery-fast" />
            At a glance — how delivery capacity works
          </Card.Title>
          <div className="mb-2">
            <Pill label="Rider app" />
            <Pill label="Three carrier modes" />
            <Pill label="Geo-matched dispatch" />
            <Pill label="Server-computed earnings" />
            <Pill label="MOMO payouts" />
          </div>
          <p className="text-muted mb-0" style={bodyText}>
            A <strong>rider</strong> is a standalone identity (own collection,
            own login) that fulfils orders through a dedicated React Native
            (Expo) app on the App Store / Play Store, belongs to one of three
            fulfilment modes — vendor in-house, authorised 3PL partner, or
            Lusuku dispatch — and earns amounts computed{" "}
            <strong>server-side</strong>, settled via MTN MOMO.
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-badge" />
            1. Onboarding &amp; verification
          </Card.Title>
          <p className="text-muted mb-2" style={bodyText}>
            Riders never self-register — an account is created <em>for</em>{" "}
            them, and the creator determines affiliation:
          </p>
          <ul className="text-muted mb-2" style={{ ...bodyText, paddingLeft: 18 }}>
            <li>
              <strong>Vendor-affiliated riders</strong> — a restaurant,
              retailer or wholesaler creates the rider from{" "}
              <strong>My Riders</strong>: mobile, first/last name, email,
              password and a carrier type (Rider, Driver, Cyclist, Foot). The
              record stores the vendor as creator, so the rider only appears
              in that vendor&rsquo;s pool and only sees that vendor&rsquo;s
              orders.
            </li>
            <li>
              <strong>Global riders</strong> — created by a super admin; the
              creator is the admin, so the rider appears in <em>every</em>{" "}
              vendor&rsquo;s selection list (labelled &ldquo;Lusuku
              Admin&rdquo;) for platform-wide coverage.
            </li>
          </ul>
          <ul className="text-muted mb-0" style={{ ...bodyText, paddingLeft: 18 }}>
            <li>
              Mobile and email are validated as unique; the password is
              bcrypt-hashed. The creator shares credentials with the rider
              outside the system; the rider then signs into the app (JWT,
              24-hour token).
            </li>
            <li>
              Riders check themselves in and out — status is{" "}
              <code>Online</code> / <code>Offline</code> / <code>Busy</code> /{" "}
              <code>inTransit</code> — and only Online, active riders are
              offered work. A status change updates the Redis cache and
              pushes a real-time <code>riderStatusUpdate</code> event to the
              dashboards.
            </li>
            <li>
              New riders start <strong>unrated</strong> (rating count 0);
              show a star score only once a customer review exists. Customers
              rate the rider from the delivered order in the consumer app
              (&ldquo;Tap to rate your rider&rdquo;); each review bumps the
              rider&rsquo;s rating count and recomputes the average
              server-side.
            </li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-vector" />
            2. The three carrier modes
          </Card.Title>
          <Table responsive hover size="sm" className="mb-2" style={{ fontSize: 12 }}>
            <thead style={theadStyle}>
              <tr>
                <th>Mode</th>
                <th>Who manages the rider</th>
                <th>Earning split</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><strong>Vendor in-house</strong></td>
                <td className="text-muted">
                  The vendor creates, edits and deletes their own riders and
                  bears the rider cost themselves.
                </td>
                <td className="text-muted">10% Lusuku / 90% rider.</td>
              </tr>
              <tr>
                <td><strong>3PL partner</strong></td>
                <td className="text-muted">
                  An authorised logistics partner manages its fleet from the
                  partner dashboard.
                </td>
                <td className="text-muted">Cost-protected fair share (section 4).</td>
              </tr>
              <tr>
                <td><strong>Lusuku dispatch</strong></td>
                <td className="text-muted">
                  Platform-managed capacity, administered by staff from
                  Active Riders.
                </td>
                <td className="text-muted">Same fair-share model as 3PL trips.</td>
              </tr>
            </tbody>
          </Table>
          <p className="text-muted mb-0" style={bodyText}>
            Access control mirrors the modes: vendors see only riders they
            created, partners only their own fleet, while platform admins see
            every rider with its affiliation labelled (global riders show as
            &ldquo;Lusuku Admin&rdquo;); middleware blocks cross-vendor access
            on every rider route, and admin-only actions (password reset,
            delete-any-rider) are permission-gated.
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-send" />
            3. Assignment &amp; the delivery run
          </Card.Title>
          <p className="text-muted mb-3" style={bodyText}>
            Assignment is <strong>durable and geo-matched</strong>: a
            restart-safe scheduler (no in-memory timers) finds riders near the
            pickup via geospatial matching (2dsphere + <code>$geoNear</code>),
            offers the job, and auto-rejects / cascades if nobody takes it.
            Acceptance is <strong>atomic</strong> (compare-and-set) — two
            riders can never win the same order.
          </p>
          <Row className="g-2">
            {[
              ["ASSIGNED", "Rider (or 3PL partner) accepts the dispatch offer"],
              ["PICKED_UP", "Rider has collected the goods from the vendor"],
              ["DELIVERED", "Customer receives the order; settlement follows"],
            ].map(([state, note], i) => (
              <Col md={4} key={state}>
                <div
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #e5e9f1",
                    borderRadius: 8,
                    padding: 12,
                    height: "100%",
                  }}
                >
                  <div style={{ fontSize: 10.5, color: "#6b7a99" }}>STEP {i + 1}</div>
                  <div style={{ fontWeight: 700, fontSize: 12.5, color: "#1a2b4a" }}>
                    {state}
                  </div>
                  <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
                    {note}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
          <ul className="text-muted mb-0" style={{ ...bodyText, paddingLeft: 18, marginTop: 12 }}>
            <li>
              Vendors can also assign manually: the picker shows their own
              plus global riders that are Online, the chosen rider is notified
              via Socket.IO, and live positions surface on the vendor and 3PL
              tracking maps.
            </li>
            <li>
              On cash-on-delivery trips the rider collects cash on
              Lusuku&rsquo;s behalf. When the rider marks the order paid, the
              server books the order&rsquo;s <strong>own total</strong> —
              never a client-typed amount — as the rider&rsquo;s COD held
              balance (a photo proof is evidence only; a mismatching client
              figure is flagged but ignored). Past a configured threshold the
              rider is flagged and must deposit via MTN MoMo before taking
              more orders; the deposit is reconciled and clears the held
              balance.
            </li>
          </ul>
        </Card.Body>
      </Card>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-money-coins" />
            4. Earnings &amp; payouts
          </Card.Title>
          <p className="text-muted mb-2" style={bodyText}>
            The fee engine is <strong>server-authoritative</strong>: the quote
            resolves a zone by road distance (urban ≤ 12 km, peri-urban
            ≤ 35 km, upcountry beyond), applies base fare + cumulative per-km
            tiers × time-of-day surge (morning and evening peaks) × route
            complexity + a flat convenience fee, rounds to the nearest UGX
            500, and clamps between a minimum fee / cost-plus-20% floor and a
            soft cap (upcountry is uncapped). At order create the server
            re-derives the quote and <strong>ignores</strong> any
            client-supplied fee or total; the rider app displays{" "}
            <strong>one server-computed earning figure</strong> everywhere.
          </p>
          <ul className="text-muted mb-3" style={{ ...bodyText, paddingLeft: 18 }}>
            <li>
              <strong>Vendor in-house trips:</strong> 10% platform / 90%
              rider — the vendor bears its own rider cost.
            </li>
            <li>
              <strong>3PL / dispatch trips:</strong> a{" "}
              <strong>cost-protected fair share</strong> — the rider always
              earns at least the full modelled delivery cost, degrading to
              roughly <strong>40 / 18 / 42</strong> (Lusuku / partner /
              rider) on short trips; on long trips the rider takes
              proportionally more. The shares always sum to the fee exactly,
              and every settlement posts a balanced double-entry journal to
              the Wallet ledger (debits equal credits).
            </li>
          </ul>
          <div style={{ background: "#f8fafc", border: "1px solid #e5e9f1", borderRadius: 8, padding: 12 }}>
            <div style={{ fontWeight: 700, color: "#1a2b4a", fontSize: 12.5 }}>
              Worked example — Kampala City Square → Ntinda
            </div>
            <div className="text-muted" style={{ fontSize: 12, marginTop: 4 }}>
              ~7.3 km by road, urban, off-peak: the customer pays{" "}
              <strong>UGX 8,000</strong> and the rider earns{" "}
              <strong>UGX 6,004</strong> (their full delivery cost), with the
              remainder shared between the 3PL partner and Lusuku — and the
              journal balances to the shilling.
            </div>
          </div>
          <p className="text-muted mt-3 mb-0" style={bodyText}>
            Payouts settle through the <strong>Wallet Service</strong> via MTN
            MOMO, with idempotent references so a retry can never double-pay.
            A per-order settlement statement (who got what) is available for
            audit. Editing the fee config needs the delivery-config permission
            (super-admins pass automatically); every change is audit-logged
            with a field-level old→new diff and takes effect immediately, no
            redeploy.
          </p>
        </Card.Body>
      </Card>

      <Card className="mb-3" style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-bullet-list-67" />
            5. Dashboard surface area
          </Card.Title>
          <p className="text-muted mb-3" style={bodyText}>
            Everything rider-related that staff, vendors and partners touch.
          </p>
          <Table responsive hover size="sm" className="mb-0" style={{ fontSize: 12 }}>
            <thead style={theadStyle}>
              <tr>
                <th>Sidebar</th>
                <th>Path</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Active Riders</td>
                <td><code>/admin/riders-table</code></td>
                <td className="text-muted">All riders + affiliation; global rider creation (staff)</td>
              </tr>
              <tr>
                <td>3PL Partners</td>
                <td><code>/admin/3pl-partners</code></td>
                <td className="text-muted">Partner onboarding (staff)</td>
              </tr>
              <tr>
                <td>My Riders (vendor / 3PL)</td>
                <td><code>/admin/my-riders</code>, <code>/admin/partner-riders</code></td>
                <td className="text-muted">Vendor in-house riders / partner fleet management</td>
              </tr>
              <tr>
                <td>Live Tracking</td>
                <td><code>/admin/rider-tracking</code></td>
                <td className="text-muted">Rider map (vendors + 3PL)</td>
              </tr>
              <tr>
                <td>Fee Settings / Transparency</td>
                <td><code>/admin/delivery-fee-settings</code>, <code>/admin/fee-transparency</code></td>
                <td className="text-muted">Fee config (permission-gated) + live split simulator</td>
              </tr>
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Card style={cardStyle}>
        <Card.Body>
          <Card.Title style={titleStyle}>
            <SectionIcon cls="nc-icon nc-bulb-63" />
            6. Quick answers
          </Card.Title>
          <QA
            q="A rider says the app shows no jobs — what do I check?"
            a={
              <>
                Their status must be <code>Online</code> and the account
                active — Offline, Busy or deactivated riders are never
                offered work. Then check affiliation: a vendor-created rider
                only sees that vendor&rsquo;s orders.
              </>
            }
          />
          <QA
            q="A vendor can't find a rider in the assign list."
            a="The picker only shows the vendor's own riders plus global riders, and only those currently Online. If the rider was created by another vendor they will never appear — that is by design, not a bug."
          />
          <QA
            q="Why does a very short trip still cost UGX 5,000?"
            a="Short urban trips are dominated by the minimum-fee floor (default UGX 5,000 urban). Fees also never drop below modelled cost plus a 20% margin, so lowering prices below that floor has no effect."
          />
          <QA
            q="Why is an upcountry delivery so expensive?"
            a="Any trip beyond ~35 km road distance prices as upcountry: a higher base fare, a UGX 15,000 minimum, and no soft cap (the cost floor governs). Staff with fee-config access can tune the upcountry tiers and thresholds."
          />
          <QA
            q="Who can change delivery pricing, and is it tracked?"
            a="Only super-admins and staff explicitly granted the delivery-config permission. Every change is audit-logged (who, when, field-level diff) and applies immediately. Use the Fee Transparency simulator to preview a 2 km and an 8 km quote before and after."
          />
          <QA
            q="A rider is blocked from taking new orders — why?"
            a="Most likely their cash-on-delivery held balance crossed the configured threshold. They must deposit the held cash via MTN MoMo; once the deposit reconciles, the flag clears automatically."
          />
          <QA
            q="Why does a rider show no star rating?"
            a="New riders start unrated — a star score only appears after the first customer review, submitted from the delivered order in the consumer app. No rating means no reviews yet, not a zero score."
          />
          {IS_DEV && (
            <div className="mt-2" style={{ fontSize: 12.5 }}>
              <Badge bg="secondary">DEV</Badge>{" "}
              <span className="text-muted">
                The backend&rsquo;s delivery-quote simulation runs the
                production fee engine locally — sanity-check pricing or split
                changes with it before they ship.
              </span>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default RiderPlatformWiki;
